import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Calendar } from '../components/ui/calendar';
import { MapPin, Users, Clock, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

export default function EventsPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [rsvping, setRsvping] = useState('');

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const { data } = await axios.get(`${API}/events`, { withCredentials: true });
      setEvents(data);
    } catch (err) { console.error(err); }
  };

  const handleRsvp = async (eventId) => {
    setRsvping(eventId);
    try {
      await axios.post(`${API}/events/${eventId}/rsvp`, {}, { withCredentials: true });
      toast.success('RSVP confirmed! See you there.');
      fetchEvents();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to RSVP');
    } finally { setRsvping(''); }
  };

  const handleCancelRsvp = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/rsvp`, { withCredentials: true });
      toast.success('RSVP cancelled');
      fetchEvents();
    } catch { toast.error('Failed to cancel RSVP'); }
  };

  const eventDates = events.map(e => new Date(e.date));
  const upcomingEvents = events.filter(e => new Date(e.date) >= new Date());

  const typeColors = {
    bhandara: { bg: 'bg-[#D4A54A]/10', text: 'text-[#D4A54A]' },
    seva: { bg: 'bg-[#7D9B6A]/10', text: 'text-[#7D9B6A]' },
    animal_care: { bg: 'bg-[#C49540]/10', text: 'text-[#C49540]' },
    group_karma: { bg: 'bg-[#8B6914]/10', text: 'text-[#8B6914]' },
  };

  return (
    <div className="min-h-screen bg-[#110A06]">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="events-page">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="mb-8">
          <motion.p variants={fadeUp} className="font-sans text-xs uppercase tracking-[0.2em] font-bold text-[#D4A54A] mb-1">Events</motion.p>
          <motion.h1 variants={fadeUp} className="font-serif text-3xl sm:text-4xl tracking-tight text-[#F5E6C8]">Karma Events</motion.h1>
          <motion.p variants={fadeUp} className="font-sans text-sm text-[#A89070] mt-1">Join curated group karma experiences</motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <div className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-4 sticky top-24">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)}
                modifiers={{ event: eventDates }}
                modifiersStyles={{ event: { fontWeight: 'bold', color: '#D4A54A', textDecoration: 'underline' } }}
                className="rounded-xl"
                data-testid="events-calendar"
              />
              <div className="mt-4 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#D4A54A]" />
                <span className="font-sans text-xs text-[#A89070]">Events scheduled</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <motion.div initial="hidden" animate="visible" variants={stagger} className="space-y-4">
              {upcomingEvents.length === 0 ? (
                <div className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-12 text-center">
                  <p className="font-sans text-[#A89070]">No upcoming events. Check back soon!</p>
                </div>
              ) : (
                upcomingEvents.map(evt => {
                  const colors = typeColors[evt.event_type] || typeColors.group_karma;
                  const isJoined = evt.participants?.includes(user?.user_id);
                  const isFull = (evt.participants?.length || 0) >= evt.max_participants;

                  return (
                    <motion.div key={evt.event_id} variants={fadeUp} data-testid={`event-card-${evt.event_id}`} className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6 hover:border-[#D4A54A]/30 transition-all duration-300">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`${colors.bg} ${colors.text} px-2.5 py-0.5 rounded-full font-sans text-xs font-bold uppercase tracking-wider`}>
                              {evt.event_type.replace('_', ' ')}
                            </span>
                          </div>
                          <h3 className="font-serif text-xl font-semibold text-[#F5E6C8] mb-2">{evt.title}</h3>
                          <p className="font-sans text-sm text-[#A89070] mb-3 line-clamp-2">{evt.description}</p>
                          <div className="flex flex-wrap gap-4 text-[#A89070]">
                            <span className="flex items-center gap-1.5 font-sans text-xs">
                              <Clock className="w-3.5 h-3.5" /> {new Date(evt.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                            <span className="flex items-center gap-1.5 font-sans text-xs">
                              <MapPin className="w-3.5 h-3.5" /> {evt.location}
                            </span>
                            <span className="flex items-center gap-1.5 font-sans text-xs">
                              <Users className="w-3.5 h-3.5" /> {evt.participants?.length || 0}/{evt.max_participants}
                            </span>
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          {isJoined ? (
                            <button onClick={() => handleCancelRsvp(evt.event_id)} data-testid={`cancel-rsvp-${evt.event_id}`} className="border border-[#C0392B]/50 text-[#C0392B] hover:bg-[#C0392B]/10 rounded-full px-5 py-2 font-sans text-sm font-medium transition-all duration-200">
                              Cancel RSVP
                            </button>
                          ) : (
                            <button
                              onClick={() => handleRsvp(evt.event_id)}
                              disabled={isFull || rsvping === evt.event_id}
                              data-testid={`rsvp-btn-${evt.event_id}`}
                              className="bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] rounded-full px-5 py-2 font-sans text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4A54A]/20 disabled:opacity-50 flex items-center gap-1.5"
                            >
                              {rsvping === evt.event_id ? 'Joining...' : isFull ? 'Full' : 'Join Event'} <ChevronRight className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
